/**
 * Single source of truth for TrainerPulse pricing tiers.
 *
 * Landing page, billing page, stripe-checkout edge function all read from here.
 * DB enum `subscription_tier` must contain every tier id.
 *
 * To change pricing:
 *   1. Update this file
 *   2. Update the DB enum via migration (if adding new tiers)
 *   3. Create matching Stripe price objects and set STRIPE_PRICE_* env vars
 *   4. Verify landing + billing + checkout flow
 */

export type SubscriptionTier =
  | "free"
  | "starter"
  | "growth"
  | "pro"
  | "studio";

export type BillingInterval = "monthly" | "annual";

export interface TierFeature {
  id: string;
  label: string;
}

export interface Tier {
  id: SubscriptionTier;
  name: string;
  tagline: string;
  monthly: number; // USD per month when billed monthly
  annual: number;  // USD per month when billed annually (effective)
  clientCap: number | "unlimited";
  clientCapLabel: string;
  trainerSeats: number; // 1 for solo tiers
  extraTrainerSeatCents?: number; // if applicable
  trialDays: number;
  highlight?: boolean;
  /** When false, checkout flow shows "Contact sales" instead of self-serve upgrade. */
  selfServe?: boolean;
  features: string[];
  /** Internal capability flags — used by feature gates in the app. */
  capabilities: readonly TierCapability[];
  /**
   * Stripe price ID env var names. Set these in Supabase edge function secrets.
   * Separate price IDs per billing interval.
   */
  stripeEnvVars: {
    monthly: string;
    annual: string;
  };
}

export type TierCapability =
  | "scheduling"
  | "clients"
  | "packages"
  | "leads"
  | "messages"
  | "client_portal"
  | "workouts"
  | "google_calendar"
  | "import"
  | "earnings"
  | "stripe_connect"
  | "pulse_check"
  | "marketing"
  | "marketing_suite"
  | "ai_assistant"
  | "health_hub"
  | "financial_intelligence"
  | "bulk_messaging"
  | "route_intelligence"
  | "multi_location"
  | "custom_branding"
  | "marketplace_listing"
  | "public_marketplace"
  | "priority_support"
  | "dedicated_success_manager"
  | "api_webhooks"
  | "white_label";

export const TIERS: Record<SubscriptionTier, Tier> = {
  free: {
    id: "free",
    name: "Free",
    tagline: "Trial only",
    monthly: 0,
    annual: 0,
    clientCap: 0,
    clientCapLabel: "No clients",
    trainerSeats: 1,
    trialDays: 0,
    features: [],
    capabilities: [],
    stripeEnvVars: { monthly: "", annual: "" },
  },

  starter: {
    id: "starter",
    name: "Starter",
    tagline: "For trainers just starting out.",
    monthly: 19,
    annual: 15,
    clientCap: 5,
    clientCapLabel: "5 active clients (+$2/extra)",
    trainerSeats: 1,
    trialDays: 14,
    features: [
      "Up to 5 active clients ($2 per extra)",
      "Core CRM, programming, and scheduling",
      "Client messaging and offline mode",
      "Client portal",
      "14-day free trial, no credit card",
    ],
    capabilities: [
      "scheduling",
      "clients",
      "packages",
      "leads",
      "messages",
      "client_portal",
      "workouts",
    ],
    stripeEnvVars: {
      monthly: "STRIPE_PRICE_STARTER_MONTHLY",
      annual: "STRIPE_PRICE_STARTER_ANNUAL",
    },
  },

  growth: {
    id: "growth",
    name: "Growth",
    tagline: "AI-powered tools for growing coaches.",
    monthly: 49,
    annual: 39,
    clientCap: 25,
    clientCapLabel: "Up to 25 active clients",
    trainerSeats: 1,
    trialDays: 14,
    highlight: true,
    features: [
      "Everything in Starter",
      "Up to 25 active clients",
      "Spotter AI (workout writing, nutrition Q&A, auto-progression)",
      "Marketing agent (campaigns, content)",
      "AI meal planning",
      "Stripe Connect + earnings and tax reports",
      "SMS reminders",
      "Group classes",
      "Health hub (Garmin, Fitbit, Apple Health)",
    ],
    capabilities: [
      "scheduling",
      "clients",
      "packages",
      "leads",
      "messages",
      "client_portal",
      "workouts",
      "google_calendar",
      "import",
      "earnings",
      "stripe_connect",
      "pulse_check",
      "marketing",
      "marketing_suite",
      "ai_assistant",
      "health_hub",
    ],
    stripeEnvVars: {
      monthly: "STRIPE_PRICE_GROWTH_MONTHLY",
      annual: "STRIPE_PRICE_GROWTH_ANNUAL",
    },
  },

  pro: {
    id: "pro",
    name: "Pro",
    tagline: "Your brand, your business, your app.",
    monthly: 89,
    annual: 69,
    clientCap: "unlimited",
    clientCapLabel: "Unlimited clients",
    trainerSeats: 1,
    trialDays: 14,
    features: [
      "Everything in Growth",
      "Unlimited clients",
      "Custom-branded app (your logo, colors, name)",
      "Custom subdomain (yourgym.trainerpulse.app)",
      "Public marketplace listing",
      "Priority support",
    ],
    capabilities: [
      "scheduling",
      "clients",
      "packages",
      "leads",
      "messages",
      "client_portal",
      "workouts",
      "google_calendar",
      "import",
      "earnings",
      "stripe_connect",
      "pulse_check",
      "marketing",
      "marketing_suite",
      "ai_assistant",
      "health_hub",
      "financial_intelligence",
      "bulk_messaging",
      "custom_branding",
      "marketplace_listing",
      "public_marketplace",
      "priority_support",
    ],
    stripeEnvVars: {
      monthly: "STRIPE_PRICE_PRO_MONTHLY",
      annual: "STRIPE_PRICE_PRO_ANNUAL",
    },
  },

  studio: {
    id: "studio",
    name: "Studio",
    tagline: "For gyms and multi-trainer teams.",
    monthly: 189,
    annual: 149,
    clientCap: "unlimited",
    clientCapLabel: "Unlimited + team seats",
    trainerSeats: 5,
    extraTrainerSeatCents: 2500,
    trialDays: 14,
    selfServe: false, // Team pricing handled via Contact Sales until we have 3+ gym inquiries.
    features: [
      "Everything in Pro",
      "5 trainer seats included (+$25 per extra)",
      "Multi-location management",
      "API and webhooks",
      "Dedicated success manager",
      "White-label App Store listing available as add-on",
    ],
    capabilities: [
      "scheduling",
      "clients",
      "packages",
      "leads",
      "messages",
      "client_portal",
      "workouts",
      "google_calendar",
      "import",
      "earnings",
      "stripe_connect",
      "pulse_check",
      "marketing",
      "marketing_suite",
      "ai_assistant",
      "health_hub",
      "financial_intelligence",
      "bulk_messaging",
      "route_intelligence",
      "multi_location",
      "custom_branding",
      "marketplace_listing",
      "public_marketplace",
      "priority_support",
      "dedicated_success_manager",
      "api_webhooks",
      "white_label",
    ],
    stripeEnvVars: {
      monthly: "STRIPE_PRICE_STUDIO_MONTHLY",
      annual: "STRIPE_PRICE_STUDIO_ANNUAL",
    },
  },
};

export const TIER_ORDER: SubscriptionTier[] = [
  "free",
  "starter",
  "growth",
  "pro",
  "studio",
];

export const PUBLIC_TIERS: SubscriptionTier[] = [
  "starter",
  "growth",
  "pro",
  "studio",
];

/** Is this a team/multi-trainer tier? */
export function isTeamTier(tier: SubscriptionTier): boolean {
  return TIERS[tier].trainerSeats > 1;
}

/** Trainer seat count for this tier (for can_add_trainer checks). */
export function getMaxTrainers(tier: SubscriptionTier): number {
  return TIERS[tier].trainerSeats;
}

/** Client cap (null = unlimited). */
export function getClientCap(tier: SubscriptionTier): number | null {
  const cap = TIERS[tier].clientCap;
  return cap === "unlimited" ? null : cap;
}

/** Does this tier include a capability? */
export function tierHasCapability(
  tier: SubscriptionTier,
  capability: TierCapability,
): boolean {
  return TIERS[tier].capabilities.includes(capability);
}

/** Get monthly price in dollars for this tier + interval. */
export function getTierPrice(
  tier: SubscriptionTier,
  interval: BillingInterval = "monthly",
): number {
  return interval === "annual" ? TIERS[tier].annual : TIERS[tier].monthly;
}

/** Display name for UI. */
export function getTierName(tier: SubscriptionTier): string {
  return TIERS[tier].name;
}

/** Tier comparison — is `a` >= `b` in capability hierarchy? */
export function tierAtLeast(
  a: SubscriptionTier,
  b: SubscriptionTier,
): boolean {
  return TIER_ORDER.indexOf(a) >= TIER_ORDER.indexOf(b);
}

/**
 * Legacy tier mapping. Some existing subscription rows in prod have tier
 * values like 'pro', 'elite', 'team_pro', 'team_elite' from an older scheme.
 * Map those to the new tier set at read time so the app keeps working.
 */
const LEGACY_TIER_MAP: Record<string, SubscriptionTier> = {
  // `pro` and `pro_solo` are now handled directly in TIERS — both map to the
  // current $89 Pro tier. Legacy `pro_plus` / `elite` users get upgraded to
  // Pro feature scope (slight upside, paid customers keep their access).
  pro_solo: "pro",
  pro_plus: "pro",
  elite: "pro",
  team_pro: "studio",
  team_pro_plus: "studio",
  team_elite: "studio",
};

/** Normalize any historical tier value to a current SubscriptionTier. */
export function normalizeTier(raw: string | null | undefined): SubscriptionTier {
  if (!raw) return "free";
  if (raw in TIERS) return raw as SubscriptionTier;
  if (raw in LEGACY_TIER_MAP) return LEGACY_TIER_MAP[raw];
  return "starter"; // safest fallback
}
